import { CSSProperties, FC, MouseEvent, useMemo, useState } from 'react';
import ChartTooltip, { ChartTooltipKey } from './ChartTooltip';

import { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';
import { createPortal } from 'react-dom';

export type HorizontalBarStackedSegment = {
  label: string;
  value: number;
  colour: string;
  striped?: boolean;
  tooltipValue?: number;
};

export type HorizontalBarStackedRow = {
  label: string;
  segments: HorizontalBarStackedSegment[];
  extraTooltipKeys?: ChartTooltipKey[];
};

type Hover = {
  rowIndex: number;
  x: number;
  y: number;
  flip: boolean;
};

const colours = {
  barBorder: '#e0e0e0',
  zeroLine: '#e0e0e0',
} as const;

const fontStyles = {
  fontSize: '12px',
  fontFamily: 'Inter, "Noto Color Emoji", sans-serif',
  cssClass: '!font-sans',
};

const TOOLTIP_OFFSET_PX = 12;
const ANNOTATIONS_SELECTOR =
  '.apexcharts-xaxis-annotations, .apexcharts-yaxis-annotations, .apexcharts-point-annotations';

// apexcharts draws annotations (the zero line) beneath the bars, so after each
// render they are raised to the top.
const layerChart = (chartEl: HTMLElement) => {
  chartEl
    .querySelectorAll<SVGGElement>(ANNOTATIONS_SELECTOR)
    .forEach((group) => group.parentElement?.appendChild(group));

  chartEl.querySelectorAll('pattern').forEach((pattern) => {
    pattern.setAttribute('patternTransform', 'rotate(90)');
    pattern
      .querySelectorAll('line')
      .forEach((line) => line.setAttribute('stroke-opacity', '0.5'));
  });
};

const tooltipKeys = (
  row: HorizontalBarStackedRow,
  format: (val: number) => string
): ChartTooltipKey[] => [
  ...row.segments
    .filter((segment) => segment.label)
    .map((segment) => ({
      label: segment.label,
      value: format(segment.tooltipValue ?? Math.abs(segment.value)),
      colour: segment.colour,
      striped: segment.striped,
    })),
  ...(row.extraTooltipKeys ?? []),
];

const sumWhere = (
  row: HorizontalBarStackedRow,
  include: (value: number) => boolean
) =>
  row.segments.reduce(
    (total, segment) =>
      include(segment.value) ? total + segment.value : total,
    0
  );

const hasNoData = (rows: HorizontalBarStackedRow[]) =>
  rows.every((row) => row.segments.every((segment) => segment.value === 0));

const segmentIndexes = (rows: HorizontalBarStackedRow[]) =>
  Array.from(
    { length: Math.max(0, ...rows.map((row) => row.segments.length)) },
    (_, i) => i
  );

const buildSeries = (rows: HorizontalBarStackedRow[]) =>
  hasNoData(rows)
    ? []
    : segmentIndexes(rows).map((seriesIndex) => ({
        name: `segment-${seriesIndex}`,
        data: rows.map((row) => {
          const segment = row.segments[seriesIndex];
          return {
            x: row.label,
            y: segment?.value ?? 0,
            fillColor: segment?.colour,
          };
        }),
      }));

const buildChartOptions = (
  rows: HorizontalBarStackedRow[],
  valueFormatter?: (val: number) => string,
  options?: ApexOptions
): ApexOptions => {
  const noData = hasNoData(rows);
  const isStriped = (seriesIndex: number) =>
    rows.some((row) => row.segments[seriesIndex]?.striped);
  const onRender = (chart: { el: HTMLElement }) => layerChart(chart.el);

  return {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      animations: { enabled: false },
      events: { mounted: onRender, updated: onRender },
    },
    legend: { show: false },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '55%',
      },
    },
    stroke: { show: true, width: 1, colors: [colours.barBorder] },
    fill: {
      opacity: 1,
      type: segmentIndexes(rows).map((i) =>
        isStriped(i) ? 'pattern' : 'solid'
      ),
      pattern: {
        style: 'slantedLines',
        width: 6,
        height: 6,
        strokeWidth: 1,
      },
    },
    grid: {
      strokeDashArray: 5,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    dataLabels: { enabled: false },
    annotations: {
      xaxis: [
        {
          x: 0,
          strokeDashArray: 0,
          borderWidth: 1,
          borderColor: colours.zeroLine,
        },
      ],
    },
    states: {
      hover: { filter: { type: 'none' } },
      active: { filter: { type: 'none' } },
    },
    // Rendered by React below rather than by apexcharts.
    tooltip: { enabled: false },
    // apexcharts' auto range doesn't account for stacked negative totals, so
    // the axis is sized to each row's full stack on either side of zero.
    yaxis: {
      min: Math.min(0, ...rows.map((row) => sumWhere(row, (v) => v < 0))),
      max: Math.max(0, ...rows.map((row) => sumWhere(row, (v) => v > 0))),
      labels: { style: fontStyles },
    },
    xaxis: {
      categories: rows.map((row) => row.label),
      labels: {
        show: !noData,
        formatter: (val: string) =>
          valueFormatter ? valueFormatter(+val) : val,
        style: fontStyles,
      },
    },
    noData: {
      text: 'No data',
      style: fontStyles,
    },
    ...options,
  };
};

export interface IProps {
  rows: HorizontalBarStackedRow[];
  height?: number | string;
  width?: number | string;
  options?: ApexOptions;
  valueFormatter?: (val: number) => string;
  tooltipFormatter?: (val: number) => string;
}

const HorizontalBarStacked: FC<IProps> = (props) => {
  const {
    rows,
    height = 230,
    width = '100%',
    options,
    valueFormatter,
    tooltipFormatter = valueFormatter,
  } = props;

  const [hover, setHover] = useState<Hover | null>(null);
  const series = useMemo(() => buildSeries(rows), [rows]);

  const chartOptions = useMemo(
    () => buildChartOptions(rows, valueFormatter, options),
    [rows, valueFormatter, options]
  );

  const onMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const bar =
      event.target instanceof Element
        ? event.target.closest('.apexcharts-bar-area[j]')
        : null;

    if (!bar) {
      setHover(null);

      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();

    setHover({
      rowIndex: Number(bar.getAttribute('j')),
      x: event.clientX,
      y: event.clientY,
      flip: event.clientX - bounds.left > bounds.width / 2,
    });
  };

  const format = tooltipFormatter ?? ((val: number) => `${val}`);
  const hoveredRow = hover ? rows[hover.rowIndex] : undefined;
  const tooltipStyle: CSSProperties | undefined = hover
    ? {
        left: hover.x,
        top: hover.y,
        transform: hover.flip
          ? `translate(calc(-100% - ${TOOLTIP_OFFSET_PX}px), ${TOOLTIP_OFFSET_PX}px)`
          : `translate(${TOOLTIP_OFFSET_PX}px, ${TOOLTIP_OFFSET_PX}px)`,
      }
    : undefined;

  return (
    <div onMouseMove={onMouseMove} onMouseLeave={() => setHover(null)}>
      <Chart
        type="bar"
        width={width}
        height={height}
        series={series}
        options={chartOptions}
      />

      {hover &&
        hoveredRow &&
        createPortal(
          <ChartTooltip
            className="fixed pointer-events-none z-sticky"
            style={tooltipStyle}
            title={hoveredRow.label}
            keys={tooltipKeys(hoveredRow, format)}
          />,
          document.body
        )}
    </div>
  );
};

export default HorizontalBarStacked;
